import React from "react";
import { motion } from "framer-motion";
import { Overline } from "@/components/brand/SectionDivider";
import { AmbientLight } from "@/components/brand/AmbientLight";
import ChapterMark from "@/components/brand/ChapterMark";
import { ConcentricMonogram } from "@/components/brand/ConcentricMonogram";

const PILLARS = [
  {
    n: "01",
    tag: "Representation",
    title: "Policyholders. Only.",
    body:
      "The firm represents homeowners, businesses, and condominium associations. Not insurance carriers — by principle, not by policy.",
  },
  {
    n: "02",
    tag: "Structure",
    title: "A firm, not a freelancer.",
    body:
      "Documented methodology, multi-disciplinary review, structured intake. Files move through a system — not a single individual.",
  },
  {
    n: "03",
    tag: "Discipline",
    title: "Document. Negotiate. Recover.",
    body:
      "Investigate the loss, document the scope, negotiate the position, recover what the policy was written to pay. Always in that order.",
  },
];

export default function Distinction() {
  return (
    <section
      data-testid="distinction-section"
      className="relative py-24 sm:py-32 lg:py-52 overflow-hidden bg-midnight-deeper"
    >
      <AmbientLight size={900} color="blue" intensity={0.45} style={{ top: "12%", left: "-18%" }} />
      <AmbientLight size={620} color="gold" intensity={0.18} style={{ bottom: "8%", right: "-6%" }} />

      {/* Architectural monogram backdrop */}
      <div
        className="hidden lg:block pointer-events-none absolute -right-40 top-1/2 -translate-y-1/2 opacity-25"
        aria-hidden
      >
        <ConcentricMonogram size={780} variant="ghost" glowColor="blue" rotateRings />
      </div>

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <ChapterMark index="01" label="The Distinction" className="mb-14" />

        <div className="grid lg:grid-cols-12 gap-16 lg:gap-24">
          <div className="lg:col-span-5">
            <h2 className="font-display-tight text-white text-[clamp(2.5rem,5.2vw,4.75rem)] leading-[0.96] tracking-[-0.045em]">
              Three principles
              <br />
              <span className="gold-metallic font-display font-light">that define the firm.</span>
            </h2>
            <div className="mt-10 h-px w-16 bg-gradient-to-r from-gold via-glow/60 to-transparent" />
            <p className="mt-10 text-ag-text-secondary text-base lg:text-[1.05rem] leading-[1.7] font-light max-w-md">
              Public adjusting is a profession of structure, evidence, and advocacy. AG Public Adjusting
              practices it as a firm — not as a personality, and not as a service of convenience.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="divide-y divide-white/[0.06]">
              {PILLARS.map((p, i) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                  className="group relative py-10 lg:py-14 first:pt-0"
                  data-testid={`distinction-pillar-${i}`}
                >
                  {/* Mobile: stack vertically. Desktop: 2 + 10 col grid */}
                  <div className="lg:grid lg:grid-cols-12 lg:gap-10">
                    {/* Index header — full width on mobile, column on desktop */}
                    <div className="lg:col-span-2 flex items-center gap-4 lg:block mb-5 lg:mb-0">
                      <div className="font-display-tight gold-metallic text-4xl sm:text-5xl lg:text-5xl leading-none tracking-[-0.05em]">
                        {p.n}
                      </div>
                      <div className="h-px w-10 lg:w-10 bg-gold/40 group-hover:w-16 group-hover:bg-gold transition-all duration-700 lg:mt-3" />
                      <div className="font-mono text-[0.55rem] text-ag-text-muted tracking-[0.3em] uppercase lg:mt-3">
                        {p.tag}
                      </div>
                    </div>

                    {/* Content column */}
                    <div className="lg:col-span-10">
                      <h3 className="font-display text-2xl md:text-3xl lg:text-[2.2rem] text-white tracking-[-0.03em] mb-4 lg:mb-5 font-light leading-[1.15]">
                        {p.title}
                      </h3>
                      <p className="text-ag-text-secondary text-[0.95rem] lg:text-[1.02rem] leading-[1.75] font-light max-w-2xl">
                        {p.body}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
